import { ReactNode, useMemo, useState } from "react";
import { KpiSeries, NormalizedSeriesData, SeriesPoint } from "../types";
import { normalizeSeriesData, transposeSeriesData } from "../utils";
import "./KpiRawDataCards.scss";
import { IconChevronDown } from "../../../../common/icons/IconChevronDown/IconChevronDown";
import NoDataValue from "../../../../common/components/NoDataValue/NoDataValue";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/keyMetrics";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { trackKpiRawDataCollapse } from "../../downlineProfileTealium";
interface KpiRawDataCardsProps {
  series?: KpiSeries[] | null; // from Performance History API
  selectedKpis?: string[];
  selectedPerformanceYear?: number;
  locale?: string;
  endPeriod?: number; // upper bound (last closed period)
  startPeriod?: number; // ABO entry period (YYYYMM); rows before this are omitted
  isLoading?: boolean;
}

interface CollapsibleComponentProps {
  header?: string;
  children?: ReactNode;
  onToggle?: (isOpen: boolean) => void;
}
const CollapsibleComponent = ({ header, children, onToggle }: CollapsibleComponentProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const handleToggle = () => {
    setIsOpen(!isOpen);
    onToggle?.(!isOpen);
  };
  return (
    <>
      <div className={`collapsible-component ${isOpen ? "collapsible-component--open" : ""}`}>
        <button className="collapsible-component__header" onClick={() => handleToggle()}>
          <p>{header}</p> <IconChevronDown />
        </button>
        <div className="collapsible-component__section">
          <div>{children}</div>
        </div>
      </div>
    </>
  );
};

interface KpiRawDataCardsRowProps {
  month?: string;
  value?: string;
}
const KpiRawDataCardsRow = ({ month, value }: KpiRawDataCardsRowProps) => {
  return (
    <div className="kpi-raw-data-cards-row">
      <p>{month}</p>
      <p>{value ?? <NoDataValue label={"No Data"} />}</p>
    </div>
  );
};

const KpiRawDataCardsLoader = () => {
  return (
    <div className="collapsible-component collapsible-component--skeleton">
      <div className="collapsible-component__skeleton-header">
        <LeafRectangleLoader isLoading width="8rem" height="1.5rem" />
      </div>
      <div className="collapsible-component__skeleton-body">
        {Array.from({ length: 9 }).map((_, j) => (
          <div key={j} className="kpi-raw-data-cards-row">
            <LeafRectangleLoader isLoading width="4rem" height="1rem" />
            <LeafRectangleLoader isLoading width="4rem" height="1rem" />
          </div>
        ))}
      </div>
    </div>
  );
};
const KpiRawDataCards = ({
  series,
  selectedKpis,
  selectedPerformanceYear,
  locale,
  endPeriod,
  startPeriod,
  isLoading,
}: KpiRawDataCardsProps) => {
  const { t } = useTranslation(["keyMetrics"]);
  const normalizeSeries: NormalizedSeriesData[] = useMemo(() => {
    return normalizeSeriesData(series, selectedKpis, selectedPerformanceYear, locale, endPeriod, startPeriod);
  }, [series, selectedKpis, selectedPerformanceYear, locale, endPeriod, startPeriod]);
  const transposedSeries = transposeSeriesData(normalizeSeries, selectedKpis);

  if (isLoading) {
    return (
      <>
        {[...Array(4)].map((_, i) => {
          return <KpiRawDataCardsLoader key={i} />;
        })}
      </>
    );
  }
  return (
    <>
      {transposedSeries.map((row) => {
        return (
          <CollapsibleComponent
            key={row.kpi}
            header={t(row.kpi, defaultContent[row.kpi] ?? row.kpi)}
            onToggle={(isOpen) => trackKpiRawDataCollapse(row.kpi, isOpen ? "expand" : "collapse")}
          >
            {row.values.map((v) => {
              return <KpiRawDataCardsRow key={v.month} month={v.monthLabel} value={v.point?.value.formatted} />;
            })}
          </CollapsibleComponent>
        );
      })}
    </>
  );
};

export default KpiRawDataCards;
